import React, { useState, useEffect, useRef } from 'react';
import { parseCSVToProject, validateCSVStructure } from '../utils/csvParser';
import ipc from '../services/ipc';
import { registerBridge } from '../services/agent/actionBridge';
import { useModal } from './ModalSystem';
import AITemplateBuilder from './AITemplateBuilder';
import BatpacDesigner from './BatpacDesigner';
import logoImg from '../assets/logo_transparent.png';
import useSavedProjects from '../hooks/useSavedProjects';
import { TEMPLATE_CATALOG, BUILTIN_TEMPLATES } from '../services/templateCatalog';
import { buildProjectFromTemplate } from '../services/templateProject';
import { buildConfigurationProject } from '../services/batpacDesign';
import { freeProjectName } from '../services/projectIdentity';
import { useCopilot } from './Copilot/CopilotProvider';

const isWeb = process.env.REACT_APP_WEB === '1';

// Recent projects shown before "Show all".
const RECENT_LIMIT = 5;

// =============================================================================
// ProjectStartup component
// =============================================================================

const ProjectStartup = ({ onProjectSelected, onOpenSaved }) => {
  const { showAlert, showConfirm } = useModal();
  // The copilot floats over the right of this screen; the screen makes room for it.
  const { open: copilotOpen } = useCopilot();
  const { projects: savedProjects, refresh: refreshSaved, remove: removeSaved } = useSavedProjects();
  const [showAllRecent, setShowAllRecent] = useState(false);
  // Built-in template files that a customized copy saved in this browser replaces.
  const [overriddenTemplates, setOverriddenTemplates] = useState([]);

  const [showProcessTree, setShowProcessTree] = useState(false);
  const [showBatpac, setShowBatpac] = useState(false);
  // The design picked on the BatPaC screen, waiting for a project name.
  const [batpacChoice, setBatpacChoice] = useState(null); // { template, entry, config, material }
  const [showNamingDialog, setShowNamingDialog] = useState(false);
  const [showAIBuilder, setShowAIBuilder] = useState(false);
  const [aiBuilderDraft, setAiBuilderDraft] = useState(null); // { template, draftId }
  const [aiDrafts, setAiDrafts] = useState([]);

  // Naming dialog state
  const [projectName, setProjectName] = useState('');
  const [projectCategory, setProjectCategory] = useState('Recycling');
  const [projectChemistry, setProjectChemistry] = useState('Li-NMC');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [csvParsedData, setCsvParsedData] = useState(null);
  const [csvFile, setCsvFile] = useState(null);
  const [csvTemplates, setCsvTemplates] = useState([]);
  const [showDownloads, setShowDownloads] = useState(false);
  const csvInputRef = useRef(null);

  // -- Load CSV template list + AI drafts on mount ----------------------------

  useEffect(() => {
    ipc.invoke('list-csv-templates').then(setCsvTemplates).catch(() => {});
    ipc.invoke('list-ai-drafts').then(setAiDrafts).catch(() => {});
    refreshSaved();
    ipc.invoke('list-user-templates')
      .then((files) => setOverriddenTemplates((Array.isArray(files) ? files : [])
        .filter((f) => BUILTIN_TEMPLATES.some((t) => t.template === f))))
      .catch(() => {});
  }, [refreshSaved]);

  // Let the copilot / walkthrough move between the startup sub-screens. Registered
  // every render (like App's bridge) so the closures stay fresh after a remount.
  useEffect(() => {
    registerBridge({
      actions: {
        // 'samples' (alias 'templates'), 'batpac' or 'landing'.
        startupNavigate: ({ screen } = {}) => {
          resetNamingDialog();
          setShowAIBuilder(false);
          setShowProcessTree(screen === 'samples' || screen === 'templates');
          setShowBatpac(screen === 'batpac');
          return { ok: true, screen: screen || 'landing' };
        },
      },
    });
  });

  // -- Start options ----------------------------------------------------------

  const handleLoadSaved = async () => {
    try {
      const result = await ipc.invoke('show-open-dialog', {
        title: 'Open Saved Project',
        filters: [{ name: 'LiTeCAB Projects', extensions: ['json'] }],
        properties: ['openFile'],
      });
      if (result?.filePaths?.length > 0) {
        const data = await ipc.invoke('load-project', result.filePaths[0]);
        onProjectSelected(data, 'existing');
      }
    } catch (err) {
      showAlert('Failed to open project: ' + err.message, { title: 'Error', type: 'error' });
    }
  };

  const handleOpenRecent = async (entry) => {
    if (onOpenSaved) await onOpenSaved(entry.filePath);
  };

  const handleDeleteRecent = async (entry) => {
    const where = isWeb ? 'this browser' : 'your saved projects';
    const ok = await showConfirm(
      `Delete "${entry.name}" from ${where}?\n\nThis can't be undone. To keep a copy, open it and choose "Download this project" first.`,
      { title: 'Delete Saved Project', type: 'warning', confirmText: 'Delete', cancelText: 'Cancel' }
    );
    if (!ok) return;
    try {
      await removeSaved(entry);
    } catch (err) {
      showAlert('Failed to delete project: ' + err.message, { title: 'Error', type: 'error' });
    }
  };

  const handleSeeSamples = () => {
    setShowBatpac(false);
    setShowProcessTree(true);
  };

  const handleDesignWithBatpac = () => {
    setShowProcessTree(false);
    setShowBatpac(true);
  };

  // Drop the customized copy so the shipped template loads again.
  const handleRestoreBuiltin = async (proc) => {
    const ok = await showConfirm(
      `A customized copy of "${proc.name}" saved in ${isWeb ? 'this browser' : 'your templates folder'} is used instead of the built-in template.\n\nRestore the built-in template? The customized copy is deleted. Projects already made from it are not changed.`,
      { title: 'Restore Built-in Template', type: 'warning', confirmText: 'Restore', cancelText: 'Cancel' }
    );
    if (!ok) return;
    try {
      const result = await ipc.invoke('delete-user-template', proc.template);
      if (!result?.success) throw new Error(result?.error || 'Delete failed');
      setOverriddenTemplates((prev) => prev.filter((f) => f !== proc.template));
    } catch (err) {
      showAlert('Failed to restore template: ' + err.message, { title: 'Error', type: 'error' });
    }
  };

  const handleStartBlank = () => {
    resetNamingDialog();
    setShowNamingDialog(true);
  };

  // -- Template selection -----------------------------------------------------

  const handleProcessSelection = async (processInfo, categoryName) => {
    try {
      const template = await ipc.invoke('load-template', processInfo.template);
      setSelectedTemplate({ template, processInfo, categoryName });
      setProjectName(await freeProjectName(template.project_name || processInfo.name));
      setProjectChemistry(processInfo.chemistry || 'Li-NMC');
      setProjectCategory(categoryName);
      setShowNamingDialog(true);
    } catch (err) {
      showAlert('Failed to load template: ' + err.message, { title: 'Error', type: 'error' });
    }
  };

  // -- Project creation -------------------------------------------------------

  const handleProjectNameSubmit = () => {
    if (!projectName.trim()) {
      showAlert('Please enter a project name.', { title: 'Validation', type: 'warning' });
      return;
    }

    // A new project never replaces a saved one, even under the same name: each
    // is saved under its own id (projectIdentity.js). The name field starts on a
    // free name so the Recent list tells them apart.

    // Route to the correct creation path
    if (csvParsedData) {
      createFromCSV();
    } else if (batpacChoice) {
      createFromBatpac();
    } else if (selectedTemplate?.template) {
      createFromTemplate();
    } else {
      createBlankProject();
    }
  };

  const createFromTemplate = () => {
    const { template, processInfo, categoryName } = selectedTemplate;

    try {
      const project = buildProjectFromTemplate(template, {
        name: projectName.trim(),
        file: processInfo.template || null, // null for an uploaded template
        chemistry: processInfo.chemistry || projectChemistry,
        category: categoryName,
      });
      validateAndEmit(project, 'template');
    } catch (err) {
      showAlert('Failed to create project: ' + err.message, { title: 'Error', type: 'error' });
    }
  };

  const createFromBatpac = () => {
    const { template, entry, config, material } = batpacChoice;
    try {
      const project = buildConfigurationProject(template, config, material, {
        name: projectName.trim(),
        file: entry.template,
      });
      validateAndEmit(project, 'template');
    } catch (err) {
      showAlert('Failed to create project: ' + err.message, { title: 'Error', type: 'error' });
    }
  };

  // "Use this one" on the BatPaC screen: name the project, then create it.
  const handleUseBatpac = async (choice) => {
    const own = Object.keys(choice.material || {}).length > 0;
    const name = `${choice.entry.label} ${choice.config.label.toLowerCase()}${own ? ', my cathode' : ''}`;
    setBatpacChoice(choice);
    setProjectName(await freeProjectName(name));
    setShowNamingDialog(true);
  };

  const createBlankProject = () => {
    const project = {
      project_name: projectName.trim(),
      project_info: {
        name: projectName.trim(),
        description: `${projectCategory} — ${projectChemistry}`,
        chemistry: projectChemistry,
        category: projectCategory,
        battery_modules: 1,
        scaling_factor: 1.0,
        // Per kWh is the default basis. Custom projects have no energy density
        // yet → Results shows a per-kg fallback with a prompt to enter one.
        fu_basis: 'energy',
        energy_density_wh_per_kg: null,
        created: new Date().toISOString(),
      },
      functional_unit: {},
      processes: {
        P001: {
          id: 'P001',
          name: 'New_Process',
          process_type: 'unit_process',
          manufacturing_category: projectCategory.toLowerCase(),
          parameters: { duration_hours: 1, temperature_c: 25 },
          input_materials: [],
          products: [],
          lci_inputs: [],
          next_process_ids: [],
          description: 'Edit this process or add new ones',
        },
      },
      analysis_results: { lca: null, tea: null },
    };

    validateAndEmit(project, 'new');
  };

  const createFromCSV = () => {
    try {
      const project = parseCSVToProject(
        csvParsedData.csvText,
        projectName.trim(),
        projectChemistry,
        projectCategory
      );
      validateAndEmit(project, 'csv');
    } catch (err) {
      showAlert('Failed to create project from CSV: ' + err.message, { title: 'CSV Error', type: 'error' });
    }
  };

  const validateAndEmit = (project, source) => {
    if (!project.project_info?.name) throw new Error('Missing project name');
    if (!project.processes || Object.keys(project.processes).length === 0) throw new Error('No processes');

    resetNamingDialog();
    setShowProcessTree(false);
    setShowBatpac(false);
    onProjectSelected(project, source);
  };

  // -- CSV upload -------------------------------------------------------------

  const handleCsvUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const isJSON = file.name.toLowerCase().endsWith('.json');
    const isCSV = file.name.toLowerCase().endsWith('.csv');

    if (!isJSON && !isCSV) {
      showAlert('Please select a valid CSV or JSON file.', { title: 'Invalid File', type: 'warning' });
      return;
    }

    setCsvFile(file);
    const reader = new FileReader();
    reader.onload = async (e) => {
      if (file.name.endsWith('.json')) {
        try {
          const jsonData = JSON.parse(e.target.result);

          // Validate JSON template structure
          if (!jsonData.processes || !jsonData.project_name) {
            showAlert('Invalid template file. Missing valid "project_name" or "processes".', { title: 'Template Error', type: 'error' });
            setCsvFile(null);
            return;
          }

          // Check if this replaces an existing internal template
          const internalTemplateId = BUILTIN_TEMPLATES
            .find(p => p.name === jsonData.project_name || p.id === jsonData.template_id);

          if (internalTemplateId) {
            const ok = await showConfirm(
              `A built-in template named "${jsonData.project_name}" already exists.\n\nDo you want to use your uploaded version instead?`,
              { title: 'Replace Template?', type: 'warning' }
            );
            if (!ok) {
              setCsvFile(null);
              return;
            }
          }

          // Treat as a "Selected Template" but with custom data
          setSelectedTemplate({
            template: jsonData,
            processInfo: {
              name: jsonData.project_name,
              chemistry: jsonData.project_info?.chemistry || 'Custom',
              description: jsonData.description || 'Imported Template'
            },
            categoryName: jsonData.project_info?.category || 'Imported'
          });

          setProjectName(await freeProjectName(jsonData.project_name));
          if (jsonData.project_info?.chemistry) setProjectChemistry(jsonData.project_info.chemistry);

          setShowNamingDialog(true);
          return;

        } catch (err) {
          showAlert('Failed to parse JSON: ' + err.message, { title: 'JSON Error', type: 'error' });
          setCsvFile(null);
          return;
        }
      }

      const csvText = e.target.result;
      const validation = validateCSVStructure(csvText);
      if (!validation.valid) {
        showAlert(`CSV Error: ${validation.error}\n\n${validation.suggestion || ''}`, { title: 'CSV Validation', type: 'error' });
        setCsvFile(null);
        return;
      }

      setCsvParsedData({ csvText, fileName: file.name.replace('.csv', ''), processCount: validation.processCount });
      setProjectName(await freeProjectName(file.name.replace('.csv', '').replace(/_/g, ' ')));
      inferFromFilename(file.name.toLowerCase());
      setShowNamingDialog(true);
    };
    reader.readAsText(file);
  };

  // -- CSV template download --------------------------------------------------

  const handleDownloadCSV = async (filename) => {
    try {
      const csvContent = await ipc.invoke('get-csv-template', filename);
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      showAlert('Failed to download template: ' + err.message, { title: 'Error', type: 'error' });
    }
  };

  const inferFromFilename = (name) => {
    if (name.includes('nmc111')) setProjectChemistry('NMC111');
    else if (name.includes('nmc622')) setProjectChemistry('NMC622');
    else if (name.includes('nmc811')) setProjectChemistry('NMC811');
    else if (name.includes('lfp')) setProjectChemistry('LFP');
    else setProjectChemistry('Li-NMC');

    if (name.includes('recycling') || name.includes('braws')) setProjectCategory('Recycling');
    else if (name.includes('manufacturing')) setProjectCategory('Manufacturing');
    else setProjectCategory('Recycling');
  };

  // -- AI Template Builder ----------------------------------------------------

  const handleAITemplateReady = (project) => {
    setShowAIBuilder(false);
    setAiBuilderDraft(null);
    // Remove the draft from the list since it's been loaded
    if (aiBuilderDraft?.draftId) {
      ipc.invoke('delete-ai-draft', aiBuilderDraft.draftId).catch(() => {});
      setAiDrafts(prev => prev.filter(d => d.draftId !== aiBuilderDraft.draftId));
    }
    onProjectSelected(project, 'template');
  };

  const handleResumeDraft = (draft) => {
    setAiBuilderDraft({ template: draft.data, draftId: draft.draftId });
    setShowAIBuilder(true);
  };

  const handleDeleteDraft = async (draftId, e) => {
    e.stopPropagation();
    await ipc.invoke('delete-ai-draft', draftId).catch(() => {});
    setAiDrafts(prev => prev.filter(d => d.draftId !== draftId));
  };

  const handleDownloadBlankTemplate = () => {
    const blank = {
      project_name: 'My Custom Process',
      description: 'Custom process template',
      version: '1.0',
      functional_unit: {
        description: '1 kg of product',
        scale: 'lab',
        weight_kg: 1,
        energy_density_wh_per_kg: null,
        feedstock_type: 'Custom feedstock',
      },
      processes: {
        P001: {
          id: 'P001',
          name: 'Process_Step_1',
          process_type: 'unit_process',
          manufacturing_category: 'Manufacturing',
          hierarchy_level: 0,
          is_side_input: false,
          energy_input_kwh: 0,
          equipment: '',
          parameters: { duration_hours: 1, temperature_c: 25, pressure_atm: 1 },
          next_process_ids: [],
          description: 'Describe this process step',
          products: [],
          lci_inputs: [
            { name: 'Electricity US Market', quantity: 0, unit: 'kilowatt hour', cost_per_unit: 0.071, cost_unit: 'USD/kWh' },
          ],
        },
      },
    };
    const blob = new Blob([JSON.stringify(blank, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'litecab_blank_template.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // -- Helpers ----------------------------------------------------------------

  const resetNamingDialog = () => {
    setShowNamingDialog(false);
    setSelectedTemplate(null);
    setBatpacChoice(null);
    setCsvParsedData(null);
    setCsvFile(null);
    setProjectName('');
    setProjectCategory('Recycling');
    setProjectChemistry('Li-NMC');
  };

  // -- Render: Naming dialog --------------------------------------------------

  const renderNamingDialog = () => {
    const isCSV = !!csvParsedData;
    const showDropdowns = isCSV || (!selectedTemplate && !batpacChoice);

    return (
      <div className="naming-dialog-overlay">
        <div className="naming-dialog">
          <h3>{isCSV ? 'Configure Your Project' : 'Name Your Project'}</h3>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 500 }}>Project Name:</label>
            <input
              type="text"
              value={projectName}
              onChange={e => setProjectName(e.target.value)}
              placeholder="e.g., NMC811 Manufacturing Study"
              autoFocus
              onKeyDown={e => e.key === 'Enter' && handleProjectNameSubmit()}
              style={{ width: '100%' }}
            />
          </div>

          {showDropdowns && (
            <>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 500 }}>Category:</label>
                <select
                  value={projectCategory}
                  onChange={e => setProjectCategory(e.target.value)}
                  style={{ width: '100%', padding: '8px', fontSize: '14px', borderRadius: '4px', border: '1px solid #ccc' }}
                >
                  <option value="Manufacturing">Manufacturing</option>
                  <option value="Remanufacturing">Remanufacturing</option>
                  <option value="Recycling">Recycling</option>
                  <option value="Coating / CVD">Coating / CVD</option>
                </select>
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 500 }}>Chemistry:</label>
                <select
                  value={projectChemistry}
                  onChange={e => setProjectChemistry(e.target.value)}
                  style={{ width: '100%', padding: '8px', fontSize: '14px', borderRadius: '4px', border: '1px solid #ccc' }}
                >
                  <option value="NMC111">NMC111</option>
                  <option value="NMC622">NMC622</option>
                  <option value="NMC811">NMC811</option>
                  <option value="LFP">LFP</option>
                  <option value="Li-NMC">Li-NMC (General)</option>
                  <option value="Custom">Custom</option>
                  <option value="NCA">NCA</option>
                  <option value="LCO">LCO</option>
                </select>
              </div>
            </>
          )}

          <div className="naming-dialog-actions">
            <button onClick={resetNamingDialog}>Cancel</button>
            <button className="primary" onClick={handleProjectNameSubmit}>Create Project</button>
          </div>
        </div>
      </div>
    );
  };

  // -- Render: Samples --------------------------------------------------------

  const renderProcessTree = () => (
    <div className="process-tree-selection" data-coach="start-templates">
      {/* Back link */}
      <button
        onClick={() => setShowProcessTree(false)}
        style={{
          background: 'none', border: 'none', color: '#1a73e8', cursor: 'pointer',
          fontSize: '14px', fontWeight: 500, padding: '6px 0', display: 'flex', alignItems: 'center', gap: '4px',
          marginBottom: '16px',
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="15 18 9 12 15 6" />
        </svg>
        Back
      </button>

      {/* Title + subtitle */}
      <h2 style={{ margin: '0 0 4px', fontSize: '1.4em', fontWeight: 600 }}>Samples</h2>
      <p style={{ color: '#5f6368', fontSize: '13px', margin: '0 0 20px' }}>
        Process templates with every input linked to carbon and price data. Open one and edit it.
      </p>

      <div className="process-categories">
        {Object.entries(TEMPLATE_CATALOG).map(([key, category]) => (
          <div key={key} className="category-section">
            <div className="category-header" style={{ borderLeft: `4px solid ${category.color}` }}>
              <span className="category-name">{category.name}</span>
            </div>

            <div className="subcategories">
              {Object.entries(category.subcategories).map(([subKey, sub]) => (
                <div key={subKey} className="subcategory">
                  <div className="subcategory-header"><span>{sub.name}</span></div>
                  <div className="processes">
                    {sub.processes.map(proc => (
                      <div
                        key={proc.id}
                        className="process-option selectable"
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                      >
                        <div style={{ flex: 1 }} onClick={() => handleProcessSelection(proc, category.name)}>
                          <div className="process-name">{proc.name}</div>
                          <div className="process-description">{proc.description}</div>
                          {overriddenTemplates.includes(proc.template) && (
                            <div className="template-override-note">
                              Using a customized copy saved in {isWeb ? 'this browser' : 'your templates folder'}, not the built-in.
                              <button
                                type="button"
                                className="template-override-restore"
                                onClick={(e) => { e.stopPropagation(); handleRestoreBuiltin(proc); }}
                              >
                                Restore built-in
                              </button>
                            </div>
                          )}
                        </div>
                        <button
                          title="Download as CSV"
                          onClick={(e) => { e.stopPropagation(); handleDownloadCSV(proc.csv); }}
                          style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            padding: '4px 8px', color: '#888', fontSize: '16px',
                            flexShrink: 0, lineHeight: 1,
                          }}
                          onMouseOver={e => e.currentTarget.style.color = '#1a73e8'}
                          onMouseOut={e => e.currentTarget.style.color = '#888'}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                            <polyline points="7 10 12 15 17 10" />
                            <line x1="12" y1="15" x2="12" y2="3" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // -- Render: Recent projects ------------------------------------------------

  const renderRecentProjects = () => {
    const shown = showAllRecent ? savedProjects : savedProjects.slice(0, RECENT_LIMIT);
    const hidden = savedProjects.length - shown.length;
    return (
      <div className="startup-recent">
        <div className="startup-recent-head">Recent projects</div>
        <ul className={`startup-recent-list${showAllRecent ? ' expanded' : ''}`}>
          {shown.map((p) => (
            <li key={p.filePath} className="startup-recent-item">
              <button
                type="button"
                className="startup-recent-open"
                onClick={() => handleOpenRecent(p)}
                title={p.lastSaved ? `Saved ${new Date(p.lastSaved).toLocaleString()}` : ''}
              >
                <span className="startup-recent-name">{p.name}</span>
                <span className="startup-recent-meta">
                  {[p.chemistry, p.lastSaved && new Date(p.lastSaved).toLocaleDateString()].filter(Boolean).join(' · ')}
                </span>
              </button>
              <button
                type="button"
                className="startup-recent-delete"
                onClick={() => handleDeleteRecent(p)}
                title={`Delete "${p.name}"`}
                aria-label={`Delete ${p.name}`}
              >
                &times;
              </button>
            </li>
          ))}
        </ul>
        {(hidden > 0 || showAllRecent) && savedProjects.length > RECENT_LIMIT && (
          <button type="button" className="startup-recent-more" onClick={() => setShowAllRecent((v) => !v)}>
            {showAllRecent ? 'Show fewer' : `Show all ${savedProjects.length}`}
          </button>
        )}
      </div>
    );
  };

  // -- Render: Saved AI drafts ------------------------------------------------

  const renderDrafts = () => (
    <div className="startup-drafts">
      <div className="startup-recent-head">Your saved drafts</div>
      <ul className="startup-recent-list">
        {aiDrafts.map((draft) => (
          <li key={draft.draftId} className="startup-recent-item">
            <button type="button" className="startup-recent-open" onClick={() => handleResumeDraft(draft)}>
              <span className="startup-recent-name">{draft.name}</span>
              <span className="startup-recent-meta">
                {draft.processCount} process{draft.processCount !== 1 ? 'es' : ''} · saved {new Date(draft.savedAt).toLocaleDateString()}
              </span>
            </button>
            <button
              type="button"
              className="startup-recent-delete"
              onClick={(e) => handleDeleteDraft(draft.draftId, e)}
              title={`Delete draft "${draft.name}"`}
              aria-label={`Delete draft ${draft.name}`}
            >
              &times;
            </button>
          </li>
        ))}
      </ul>
    </div>
  );

  // -- Render: Start cards ----------------------------------------------------

  // Three ways in. All end in the same kind of project and the same results;
  // they differ in where the amounts and the carbon and price links come from.
  // The cards share one layout so the row stays even; the file options sit in
  // the link row below.
  const START_CARDS = [
    {
      coach: 'start-batpac',
      title: 'Design with BatPaC',
      desc: 'Pick a cathode or enter your own numbers. BatPaC sets the material amounts.',
      onClick: handleDesignWithBatpac,
      primary: true,
      icon: (
        <>
          <line x1="4" y1="21" x2="4" y2="14" />
          <line x1="4" y1="10" x2="4" y2="3" />
          <line x1="12" y1="21" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12" y2="3" />
          <line x1="20" y1="21" x2="20" y2="16" />
          <line x1="20" y1="12" x2="20" y2="3" />
          <line x1="1" y1="14" x2="7" y2="14" />
          <line x1="9" y1="8" x2="15" y2="8" />
          <line x1="17" y1="16" x2="23" y2="16" />
        </>
      ),
    },
    {
      coach: 'start-samples',
      title: 'See samples',
      desc: 'NMC111, NMC622, NMC811 and LFP cells, and BRAWS recycling, ready to edit.',
      onClick: handleSeeSamples,
      icon: (
        <>
          <rect x="3" y="3" width="7" height="7" rx="1" />
          <rect x="14" y="3" width="7" height="7" rx="1" />
          <rect x="3" y="14" width="7" height="7" rx="1" />
          <rect x="14" y="14" width="7" height="7" rx="1" />
        </>
      ),
    },
    {
      coach: 'start-ai',
      title: 'Generate your own',
      desc: 'Describe any process or upload a document. The copilot drafts the steps.',
      onClick: () => setShowAIBuilder(true),
      icon: (
        <path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z M19 16l.8 2.2L22 19l-2.2.8L19 22l-.8-2.2L16 19l2.2-.8z" />
      ),
    },
  ];

  const renderStartCards = () => (
    <div className="start-cards" data-coach="start-options">
      {START_CARDS.map((card) => (
        <button
          key={card.coach}
          type="button"
          className={`start-card${card.primary ? ' start-card--primary' : ''}`}
          onClick={card.onClick}
          data-coach={card.coach}
        >
          <span className="start-card-icon" aria-hidden="true">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              {card.icon}
            </svg>
          </span>
          <span className="start-card-text">
            <span className="start-card-title">{card.title}</span>
            <span className="start-card-desc">{card.desc}</span>
          </span>
        </button>
      ))}
    </div>
  );

  // Open a saved file or bring a filled CSV, under the cards.
  const renderStartLinks = () => (
    <div className="startup-links">
      <button type="button" className="startup-link" onClick={handleLoadSaved} data-coach="start-load">
        Open a project file
      </button>
      <span aria-hidden="true">·</span>
      <button type="button" className="startup-link" onClick={() => csvInputRef.current?.click()}>
        Upload a CSV
      </button>
      <span aria-hidden="true">·</span>
      <button type="button" className="startup-link" onClick={() => handleDownloadCSV('blank_template.csv')}>
        Blank CSV
      </button>
      <input ref={csvInputRef} type="file" accept=".csv,.json" onChange={handleCsvUpload} style={{ display: 'none' }} />
    </div>
  );

  // -- Render: Main -----------------------------------------------------------

  // AI Template Builder modal (overlays on top)
  if (showAIBuilder) {
    return (
      <AITemplateBuilder
        onTemplateReady={handleAITemplateReady}
        onClose={() => { setShowAIBuilder(false); setAiBuilderDraft(null); }}
        initialTemplate={aiBuilderDraft?.template || null}
        draftId={aiBuilderDraft?.draftId || null}
      />
    );
  }

  let body;
  if (showBatpac) {
    body = <BatpacDesigner onBack={() => setShowBatpac(false)} onUse={handleUseBatpac} />;
  } else if (showProcessTree) {
    body = renderProcessTree();
  } else {
    body = (
      <>
        {renderStartCards()}
        {renderStartLinks()}
        {aiDrafts.length > 0 && renderDrafts()}
        {savedProjects.length > 0 && renderRecentProjects()}
      </>
    );
  }

  return (
    <div className={`startup-screen${copilotOpen ? ' startup-screen--copilot' : ''}`}>
      <div className={`startup-container${showBatpac || showProcessTree ? ' startup-container--sub' : ''}`}>
        <div className="startup-header">
          <img src={logoImg} alt="LiTeCAB" className="startup-logo" />
          <p className="startup-subtitle">Life Cycle Assessment & Techno-Economic Analysis for Battery Processes</p>
        </div>
        {csvFile && !showNamingDialog && <div className="startup-file-note">{csvFile.name}</div>}
        {body}
      </div>
      {showNamingDialog && renderNamingDialog()}
    </div>
  );
};

export default ProjectStartup;
